import { z } from 'zod'

export type ProgressiveLevel = 'draft' | 'partial' | 'complete'

export interface ProgressiveConfig<T extends z.ZodRawShape> {
  base: z.ZodObject<T>
  levels: Record<ProgressiveLevel, (keyof T)[]>
}

/**
 * Construye un schema con validación progresiva: el mismo objeto base se valida
 * con distinto nivel de exigencia según la posición del cliente en el pipeline.
 *
 * - `complete`  → todos los campos requeridos
 * - `forLevel(level)` → sólo los campos del nivel quedan requeridos; el resto opcional
 *
 * Patrón KYC: el backend deriva el nivel del estado de la entidad (ver `policies/`).
 */
export function createProgressiveSchema<T extends z.ZodRawShape>(config: ProgressiveConfig<T>) {
  return {
    complete: config.base,

    forLevel(level: ProgressiveLevel) {
      const required = new Set(config.levels[level])
      const shape = Object.fromEntries(
        Object.entries(config.base.shape).map(([key, schema]) =>
          required.has(key as keyof T) ? [key, schema] : [key, (schema as z.ZodTypeAny).optional()]
        )
      )
      return z.object(shape)
    },

    requiredFieldsAt(level: ProgressiveLevel) {
      return config.levels[level]
    }
  }
}
